import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';

/**
 * Per-conversation runtime state. Lives outside AgentContext so that
 * concurrent streams (e.g. user sending in conversation A and switching to B
 * while it streams) write to their own slot and never leak across views.
 */
export interface ConversationRuntime {
  isThinking: boolean;
  thinkingMessage: string;
  thinkingSteps: string[];
  isLoading: boolean;
  isUnread: boolean;
  startedAtMs: number | null;
}

interface InternalRuntime extends ConversationRuntime {
  seenEventTypes: Set<string>;
}

const EMPTY_RUNTIME: Readonly<ConversationRuntime> = Object.freeze({
  isThinking: false,
  thinkingMessage: '',
  thinkingSteps: Object.freeze([]) as readonly string[] as string[],
  isLoading: false,
  isUnread: false,
  startedAtMs: null,
});

const makeEmptyInternal = (): InternalRuntime => ({
  ...EMPTY_RUNTIME,
  seenEventTypes: new Set<string>(),
});

interface State {
  byId: Record<string, InternalRuntime>;
  // Separate refs so membership-only consumers don't re-render on every trace event.
  loadingIds: Set<string>;
  unreadIds: Set<string>;
}

const INITIAL_STATE: State = {
  byId: {},
  loadingIds: new Set<string>(),
  unreadIds: new Set<string>(),
};

type Action =
  | { type: 'startSend'; id: string }
  | { type: 'setThinking'; id: string; value: boolean }
  | { type: 'setThinkingMessage'; id: string; message: string }
  | { type: 'appendStep'; id: string; eventType: string; message: string }
  | { type: 'finishSend'; id: string; markUnread: boolean }
  | { type: 'cancelSend'; id: string }
  | { type: 'markRead'; id: string }
  | { type: 'remove'; id: string };

function getEntry(byId: State['byId'], id: string): InternalRuntime {
  return byId[id] ?? makeEmptyInternal();
}

function isEmptyRuntime(r: InternalRuntime): boolean {
  return (
    !r.isLoading &&
    !r.isThinking &&
    !r.isUnread &&
    r.thinkingMessage === '' &&
    r.thinkingSteps.length === 0 &&
    r.seenEventTypes.size === 0 &&
    r.startedAtMs === null
  );
}

function withoutKey(byId: State['byId'], id: string): State['byId'] {
  if (!(id in byId)) return byId;
  const next = { ...byId };
  delete next[id];
  return next;
}

/** Applies a new entry or drops the slot if the entry is empty (keeps state lean). */
function applyOrDrop(byId: State['byId'], id: string, next: InternalRuntime): State['byId'] {
  if (isEmptyRuntime(next)) return withoutKey(byId, id);
  return { ...byId, [id]: next };
}

/** Returns a new Set with `id` added (or the same Set if already present). */
function setWith(set: Set<string>, id: string): Set<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

/** Returns a new Set with `id` removed (or the same Set if not present). */
function setWithout(set: Set<string>, id: string): Set<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'startSend':
      return {
        byId: {
          ...state.byId,
          [action.id]: {
            ...EMPTY_RUNTIME,
            isLoading: true,
            seenEventTypes: new Set(),
            startedAtMs: Date.now(),
          },
        },
        loadingIds: setWith(state.loadingIds, action.id),
        // Sending implies engagement → clear any prior unread flag for this id.
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    case 'setThinking': {
      const prev = getEntry(state.byId, action.id);
      if (prev.isThinking === action.value) return state;
      return { ...state, byId: applyOrDrop(state.byId, action.id, { ...prev, isThinking: action.value }) };
    }
    case 'setThinkingMessage': {
      const prev = getEntry(state.byId, action.id);
      if (prev.thinkingMessage === action.message) return state;
      return { ...state, byId: applyOrDrop(state.byId, action.id, { ...prev, thinkingMessage: action.message }) };
    }
    case 'appendStep': {
      const prev = getEntry(state.byId, action.id);
      if (prev.seenEventTypes.has(action.eventType)) return state;
      const seen = new Set(prev.seenEventTypes);
      seen.add(action.eventType);
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.id]: {
            ...prev,
            seenEventTypes: seen,
            thinkingMessage: action.message,
            thinkingSteps: [...prev.thinkingSteps, action.message],
          },
        },
      };
    }
    case 'finishSend': {
      const prev = getEntry(state.byId, action.id);
      return {
        byId: applyOrDrop(state.byId, action.id, {
          ...prev,
          isLoading: false,
          isThinking: false,
          thinkingMessage: '',
          thinkingSteps: [],
          seenEventTypes: new Set(),
          startedAtMs: null,
          isUnread: action.markUnread,
        }),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: action.markUnread ? setWith(state.unreadIds, action.id) : setWithout(state.unreadIds, action.id),
      };
    }
    case 'cancelSend':
      return {
        byId: withoutKey(state.byId, action.id),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    case 'markRead': {
      const prev = getEntry(state.byId, action.id);
      if (!prev.isUnread) return state;
      return {
        ...state,
        byId: applyOrDrop(state.byId, action.id, { ...prev, isUnread: false }),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    }
    case 'remove':
      return {
        byId: withoutKey(state.byId, action.id),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    default:
      return state;
  }
}

interface AgentRuntimeContextType {
  getRuntime: (id: string | null | undefined) => ConversationRuntime;
  /** Synchronous check — true if there's a controller registered for this id (faster than reading state). */
  isInFlight: (id: string) => boolean;
  startSend: (id: string, prompt: string) => AbortSignal;
  setThinking: (id: string, value: boolean) => void;
  setThinkingMessage: (id: string, message: string) => void;
  appendStep: (id: string, eventType: string, message: string) => void;
  finishSend: (id: string, isCurrentlyActive: boolean) => void;
  cancelSend: (id: string) => string | null;
  markRead: (id: string) => void;
  removeRuntime: (id: string) => void;
  loadingIds: Set<string>;
  unreadIds: Set<string>;
}

const AgentRuntimeContext = createContext<AgentRuntimeContextType | undefined>(undefined);

export const AgentRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  // Last prompt per conversation — used to repopulate the input on cancel.
  // Lives in a ref because it doesn't drive re-renders.
  const lastPromptsRef = useRef<Map<string, string>>(new Map());

  const getRuntime = useCallback(
    (id: string | null | undefined): ConversationRuntime => {
      if (!id) return EMPTY_RUNTIME;
      return state.byId[id] ?? EMPTY_RUNTIME;
    },
    [state.byId],
  );

  // Sync read of in-flight controllers — used to dedupe rapid double-sends
  // before React has had a chance to flush the `isLoading` state.
  const isInFlight = useCallback((id: string) => controllersRef.current.has(id), []);

  const startSend = useCallback((id: string, prompt: string) => {
    const existing = controllersRef.current.get(id);
    if (existing) existing.abort();
    const controller = new AbortController();
    controllersRef.current.set(id, controller);
    lastPromptsRef.current.set(id, prompt);
    dispatch({ type: 'startSend', id });
    return controller.signal;
  }, []);

  const setThinking = useCallback((id: string, value: boolean) => {
    dispatch({ type: 'setThinking', id, value });
  }, []);

  const setThinkingMessage = useCallback((id: string, message: string) => {
    dispatch({ type: 'setThinkingMessage', id, message });
  }, []);

  const appendStep = useCallback((id: string, eventType: string, message: string) => {
    dispatch({ type: 'appendStep', id, eventType, message });
  }, []);

  const finishSend = useCallback((id: string, isCurrentlyActive: boolean) => {
    controllersRef.current.delete(id);
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'finishSend', id, markUnread: !isCurrentlyActive });
  }, []);

  const cancelSend = useCallback((id: string): string | null => {
    const controller = controllersRef.current.get(id);
    if (controller) controller.abort();
    controllersRef.current.delete(id);
    const prompt = lastPromptsRef.current.get(id) ?? null;
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'cancelSend', id });
    return prompt;
  }, []);

  const markRead = useCallback((id: string) => {
    dispatch({ type: 'markRead', id });
  }, []);

  const removeRuntime = useCallback((id: string) => {
    controllersRef.current.delete(id);
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'remove', id });
  }, []);

  const value = useMemo<AgentRuntimeContextType>(
    () => ({
      getRuntime,
      isInFlight,
      startSend,
      setThinking,
      setThinkingMessage,
      appendStep,
      finishSend,
      cancelSend,
      markRead,
      removeRuntime,
      loadingIds: state.loadingIds,
      unreadIds: state.unreadIds,
    }),
    [
      getRuntime,
      isInFlight,
      startSend,
      setThinking,
      setThinkingMessage,
      appendStep,
      finishSend,
      cancelSend,
      markRead,
      removeRuntime,
      state.loadingIds,
      state.unreadIds,
    ],
  );

  return <AgentRuntimeContext.Provider value={value}>{children}</AgentRuntimeContext.Provider>;
};

const useAgentRuntime = (): AgentRuntimeContextType => {
  const ctx = useContext(AgentRuntimeContext);
  if (!ctx) {
    throw new Error('useAgentRuntime must be used within AgentRuntimeProvider');
  }
  return ctx;
};

export default useAgentRuntime;
